import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  type Node,
  type Edge,
  Handle,
  Position,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { can } from '../../lib/roles';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import { listDivisions, createDivision, renameDivision, setDivisionStatus, type DivisionDto } from '../../lib/divisions';
import { listPositions, createPosition, type PositionDto } from '../../lib/positions';
import {
  listUsers, inviteUser, assignDivision,
  updateUserPosition, updateUserOrgLevel, type UserDto,
} from '../../lib/users';
import { listOrgLevels, type OrgLevelDto } from '../../lib/orgLevels';
import type { UserRole } from '../../lib/roles';
import { Building2, Users, Briefcase, Plus, UserPlus, X, ChevronRight, Pencil } from 'lucide-react';
import { toast } from 'sonner';

export const Route = createFileRoute('/org/')({
  component: OrgPage,
});

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  admin:    { bg: '#f3e8ff', text: '#7c3aed', border: '#c4b5fd' },
  hr:       { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  manager:  { bg: '#fef3c7', text: '#b45309', border: '#fcd34d' },
  employee: { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' },
};

// Layout constants
const DIV_W   = 200;
const DIV_H   = 90;
const PER_W   = 160;
const PER_H   = 72;
const ROOT_W  = 220;
const H_GAP   = 36;
const V_GAP   = 60;
const PER_V_GAP = 12;

// ─── Drill-down state types ───────────────────────────────────────────────────

type Crumb = { id: string | null; label: string };

type DrillState =
  | { type: 'company' }
  | { type: 'division'; divisionId: string; breadcrumbs: Crumb[] };

// ─── Helpers ─────────────────────────────────────────────────────────────────

type DivNode = DivisionDto & { children: DivNode[] };

function buildTree(divisions: DivisionDto[]): DivNode[] {
  const map = new Map<string, DivNode>();
  divisions.forEach((d) => map.set(d.id, { ...d, children: [] }));
  const roots: DivNode[] = [];
  divisions.forEach((d) => {
    if (d.parentDivisionId && map.has(d.parentDivisionId)) {
      map.get(d.parentDivisionId)!.children.push(map.get(d.id)!);
    } else {
      roots.push(map.get(d.id)!);
    }
  });
  return roots;
}

/** Width of a division subtree in "slot" units */
function subtreeSlots(node: DivNode, memberCounts: Map<string, number>): number {
  const personSlots = Math.max(1, memberCounts.get(node.id) ?? 0);
  const childrenSlots = node.children.length === 0
    ? personSlots
    : node.children.reduce((s, c) => s + subtreeSlots(c, memberCounts), 0);
  return Math.max(personSlots, childrenSlots);
}

type LayoutPos = { x: number; y: number };

/** Recursively compute division node positions (top-down) */
function layoutDivisions(
  nodes: DivNode[],
  posMap: Map<string, LayoutPos>,
  memberCounts: Map<string, number>,
  startX: number,
  depth: number,
): void {
  let cursor = startX;
  for (const node of nodes) {
    const slots = subtreeSlots(node, memberCounts);
    const totalW = slots * (PER_W + H_GAP) - H_GAP;
    const cx = cursor + totalW / 2 - DIV_W / 2;
    posMap.set(node.id, { x: cx, y: depth * (DIV_H + V_GAP + PER_H + PER_V_GAP * 2) });
    layoutDivisions(node.children, posMap, memberCounts, cursor, depth + 1);
    cursor += totalW + H_GAP;
  }
}

// ─── Node data types ──────────────────────────────────────────────────────────

type RootNodeData    = { label: string; employeeCount: number };
type DivNodeData     = { division: DivisionDto; memberCount: number; positionCount: number };
type DivRootNodeData = { division: DivisionDto; memberCount: number };
type PersonNodeData  = { employee: UserDto; positionTitle: string; orgLevelLabel: string };

// ─── Custom node: Company root ────────────────────────────────────────────────

function RootFlowNode({ data }: { data: RootNodeData }) {
  return (
    <div
      style={{ width: ROOT_W }}
      className="rounded-xl border-2 border-teal-400 bg-gradient-to-br from-teal-500 to-teal-600 shadow-lg text-white flex flex-col items-center justify-center py-3 px-4"
    >
      <Handle type="source" position={Position.Bottom} style={{ background: '#5eead4', border: 'none', width: 8, height: 8 }} />
      <div className="flex items-center gap-2">
        <Building2 size={16} className="opacity-80" />
        <span className="font-bold text-sm tracking-wide">{data.label}</span>
      </div>
      <p className="text-[11px] text-teal-100 mt-0.5">{data.employeeCount} employees</p>
    </div>
  );
}

// ─── Custom node: Division (company view) ─────────────────────────────────────

function DivFlowNode({ data }: { data: DivNodeData }) {
  const { division, memberCount, positionCount } = data;
  return (
    <div
      style={{ width: DIV_W, borderColor: '#bfdbfe' }}
      className="rounded-xl border-2 bg-white shadow-md hover:shadow-lg transition-all cursor-pointer select-none"
    >
      <Handle type="target" position={Position.Top}    style={{ background: '#93c5fd', border: 'none', width: 8, height: 8 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#93c5fd', border: 'none', width: 8, height: 8 }} />

      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b rounded-t-xl bg-blue-50 border-blue-50">
        <span className="text-[10px] font-bold font-mono bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded">
          {division.code}
        </span>
        <span className="text-xs font-semibold text-blue-900 truncate">{division.name}</span>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-around py-2 px-2">
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Users size={11} className="text-blue-400" />
          <span className="font-medium text-gray-700">{memberCount}</span>
          <span className="text-gray-400">people</span>
        </div>
        <div className="w-px h-4 bg-gray-100" />
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Briefcase size={11} className="text-blue-400" />
          <span className="font-medium text-gray-700">{positionCount}</span>
          <span className="text-gray-400">roles</span>
        </div>
      </div>

      {/* View team hint */}
      <div className="flex items-center justify-center gap-1 py-1 border-t border-gray-100 text-[10px] font-medium text-blue-500">
        <span>View team →</span>
      </div>
    </div>
  );
}

// ─── Custom node: Division root (drill-down view) ─────────────────────────────

function DivRootFlowNode({ data }: { data: DivRootNodeData }) {
  const { division, memberCount } = data;
  return (
    <div
      style={{ width: ROOT_W }}
      className="rounded-xl border-2 border-blue-500 bg-gradient-to-br from-blue-500 to-blue-700 shadow-lg text-white flex flex-col items-center justify-center py-3 px-4"
    >
      <Handle type="source" position={Position.Bottom} style={{ background: '#93c5fd', border: 'none', width: 8, height: 8 }} />
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold font-mono bg-blue-300/40 text-white px-1.5 py-0.5 rounded">
          {division.code}
        </span>
        <span className="font-bold text-sm tracking-wide truncate">{division.name}</span>
      </div>
      <p className="text-[11px] text-blue-100 mt-0.5">{memberCount} members</p>
    </div>
  );
}

// ─── Custom node: Person ──────────────────────────────────────────────────────

function PersonFlowNode({ data }: { data: PersonNodeData }) {
  const { employee, positionTitle, orgLevelLabel } = data;
  const colors = ROLE_COLORS[employee.role] ?? ROLE_COLORS.employee;
  const initials = employee.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div
      style={{ width: PER_W, borderColor: colors.border }}
      className="rounded-xl border-2 bg-white shadow-sm hover:shadow-md transition-all cursor-pointer select-none group"
    >
      <Handle type="target" position={Position.Top} style={{ background: colors.border, border: 'none', width: 6, height: 6 }} />

      <div className="flex flex-col items-center py-2.5 px-2 gap-1.5">
        {/* Avatar */}
        <div
          style={{ background: colors.bg, color: colors.text }}
          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
        >
          {initials}
        </div>

        {/* Name */}
        <p className="text-[11px] font-semibold text-gray-800 text-center leading-tight truncate w-full px-1 group-hover:text-blue-600">
          {employee.name}
        </p>

        {/* Position */}
        <p className="text-[10px] text-gray-400 text-center leading-tight truncate w-full px-1">
          {positionTitle || employee.role}
        </p>

        {/* Role + Level badges */}
        <div className="flex items-center gap-1 flex-wrap justify-center">
          <span
            style={{ background: colors.bg, color: colors.text }}
            className="text-[9px] px-1.5 py-0.5 rounded-full font-medium capitalize"
          >
            {employee.role}
          </span>
          {orgLevelLabel && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-mono">
              {orgLevelLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const NODE_TYPES = {
  root:    RootFlowNode,
  division: DivFlowNode,
  divroot: DivRootFlowNode,
  person:  PersonFlowNode,
};

// ─── Build Company Graph ──────────────────────────────────────────────────────
// Shows company root → top-level divisions → sub-divisions. No person nodes.

function buildCompanyGraph(
  divisions: DivisionDto[],
  employees: UserDto[],
  positions: PositionDto[],
  companyName: string,
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  if (divisions.length === 0) return { nodes, edges };

  const tree = buildTree(divisions);
  const memberCounts = new Map<string, number>();
  divisions.forEach((d) => {
    memberCounts.set(d.id, employees.filter((e) => e.divisionId === d.id).length);
  });

  const divPosMap = new Map<string, LayoutPos>();
  layoutDivisions(tree, divPosMap, memberCounts, 0, 1);

  let minX = Infinity, maxX = -Infinity;
  divPosMap.forEach(({ x }) => { if (x < minX) minX = x; if (x + DIV_W > maxX) maxX = x + DIV_W; });
  const rootX = (minX + maxX) / 2 - ROOT_W / 2;
  const rootY = 0;

  const rootId = '__root__';
  nodes.push({
    id: rootId,
    type: 'root',
    position: { x: rootX, y: rootY },
    data: { label: companyName, employeeCount: employees.length } as RootNodeData,
    width: ROOT_W,
    height: 60,
    style: { width: ROOT_W },
  });

  for (const div of divisions) {
    const pos = divPosMap.get(div.id) ?? { x: 0, y: 120 };
    const divMembers = employees.filter((e) => e.divisionId === div.id);
    const divPositions = positions.filter((p) => p.divisionId === div.id);

    nodes.push({
      id: div.id,
      type: 'division',
      position: pos,
      data: {
        division: div,
        memberCount: divMembers.length,
        positionCount: divPositions.length,
      } as DivNodeData,
      width: DIV_W,
      height: DIV_H,
      style: { width: DIV_W },
    });

    const sourceId = div.parentDivisionId ?? rootId;
    edges.push({
      id: `e-${sourceId}-${div.id}`,
      source: sourceId,
      target: div.id,
      type: 'smoothstep',
      style: { stroke: '#93c5fd', strokeWidth: 2 },
    });
  }

  return { nodes, edges };
}

// ─── Build Division Graph ─────────────────────────────────────────────────────
// Shows: division root → child sub-divisions + direct members by org-level tier.

// Org level tier classification by level number
function getTier(orgLevel: OrgLevelDto | undefined): 1 | 2 | 3 {
  if (!orgLevel) return 3;
  const lvl = orgLevel.level;
  if (lvl <= 2) return 1; // VP / Director
  if (lvl <= 4) return 2; // Manager / Lead
  return 3;               // Member / IC
}

function buildDivisionGraph(
  divisionId: string,
  divisions: DivisionDto[],
  employees: UserDto[],
  positions: PositionDto[],
  orgLevels: OrgLevelDto[],
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const division = divisions.find((d) => d.id === divisionId);
  if (!division) return { nodes, edges };

  // Compute member count (direct only)
  const directMembers = employees.filter((e) => e.divisionId === divisionId);
  const childDivisions = divisions.filter((d) => d.parentDivisionId === divisionId);

  // Place division root at top center
  const totalWidth = Math.max(
    ROOT_W,
    childDivisions.length * (DIV_W + H_GAP) - H_GAP,
    directMembers.length > 0 ? directMembers.length * (PER_W + H_GAP) - H_GAP : 0,
  );
  const rootX = totalWidth / 2 - ROOT_W / 2;
  const rootId = `divroot-${divisionId}`;

  nodes.push({
    id: rootId,
    type: 'divroot',
    position: { x: rootX, y: 0 },
    data: { division, memberCount: directMembers.length } as DivRootNodeData,
    width: ROOT_W,
    height: 60,
    style: { width: ROOT_W },
  });

  // Child sub-division nodes (row at y=200)
  if (childDivisions.length > 0) {
    const childTotalW = childDivisions.length * (DIV_W + H_GAP) - H_GAP;
    const childStartX = totalWidth / 2 - childTotalW / 2;

    childDivisions.forEach((child, i) => {
      const childMembers = employees.filter((e) => e.divisionId === child.id);
      const childPositions = positions.filter((p) => p.divisionId === child.id);
      const cx = childStartX + i * (DIV_W + H_GAP);

      nodes.push({
        id: child.id,
        type: 'division',
        position: { x: cx, y: 180 },
        data: {
          division: child,
          memberCount: childMembers.length,
          positionCount: childPositions.length,
        } as DivNodeData,
        width: DIV_W,
        height: DIV_H,
        style: { width: DIV_W },
      });

      edges.push({
        id: `e-${rootId}-${child.id}`,
        source: rootId,
        target: child.id,
        type: 'smoothstep',
        style: { stroke: '#93c5fd', strokeWidth: 2 },
      });
    });
  }

  // Direct members — split into tiers by org level
  if (directMembers.length > 0) {
    const tier1 = directMembers.filter((e) => getTier(orgLevels.find((l) => l.id === e.orgLevelId)) === 1);
    const tier2 = directMembers.filter((e) => getTier(orgLevels.find((l) => l.id === e.orgLevelId)) === 2);
    const tier3 = directMembers.filter((e) => getTier(orgLevels.find((l) => l.id === e.orgLevelId)) === 3);

    // Y offset: if child divisions exist, push tiers down
    const tierBaseY = childDivisions.length > 0 ? 180 + DIV_H + V_GAP : 180;

    function placeTier(members: UserDto[], yPos: number, sourceNodeIds: string[]) {
      if (members.length === 0) return;
      const tierW = members.length * (PER_W + H_GAP) - H_GAP;
      const startX = totalWidth / 2 - tierW / 2;

      members.forEach((emp, i) => {
        const perX = startX + i * (PER_W + H_GAP);
        const personId = `person-${emp.id}`;
        const empPos = positions.find((p) => p.id === emp.positionId);
        const empLvl = orgLevels.find((l) => l.id === emp.orgLevelId);

        nodes.push({
          id: personId,
          type: 'person',
          position: { x: perX, y: yPos },
          data: {
            employee: emp,
            positionTitle: empPos?.title ?? '',
            orgLevelLabel: empLvl ? `L${empLvl.level}` : '',
          } as PersonNodeData,
          width: PER_W,
          height: PER_H,
          style: { width: PER_W },
        });

        // Connect to the most relevant source (prefer first source node)
        const src = sourceNodeIds[0] ?? rootId;
        edges.push({
          id: `e-${src}-${personId}`,
          source: src,
          target: personId,
          type: 'smoothstep',
          style: { stroke: '#d1d5db', strokeWidth: 1.5 },
        });
      });
    }

    const t1Ids = tier1.map((e) => `person-${e.id}`);
    const t2Ids = tier2.map((e) => `person-${e.id}`);

    // Tier 1 (VP/Director) — connected from div root
    placeTier(tier1, tierBaseY, [rootId]);
    // Tier 2 (Manager/Lead) — connected from tier1 if exists, else root
    placeTier(tier2, tierBaseY + PER_H + V_GAP, t1Ids.length > 0 ? t1Ids : [rootId]);
    // Tier 3 (Member) — connected from tier2 if exists, else tier1 if exists, else root
    const t3Source = t2Ids.length > 0 ? t2Ids : t1Ids.length > 0 ? t1Ids : [rootId];
    placeTier(tier3, tierBaseY + (PER_H + V_GAP) * 2, t3Source);
  }

  return { nodes, edges };
}

// ─── Organogram component ─────────────────────────────────────────────────────

function Organogram({
  divisions, employees, positions, orgLevels, companyName, canManage, onAddDiv,
}: {
  divisions: DivisionDto[];
  employees: UserDto[];
  positions: PositionDto[];
  orgLevels: OrgLevelDto[];
  companyName: string;
  canManage: boolean;
  onAddDiv: () => void;
}) {
  const navigate = useNavigate();
  const [drillState, setDrillState] = useState<DrillState>({ type: 'company' });

  const { nodes, edges } = useMemo(() => {
    if (drillState.type === 'company') {
      return buildCompanyGraph(divisions, employees, positions, companyName);
    }
    return buildDivisionGraph(drillState.divisionId, divisions, employees, positions, orgLevels);
  }, [drillState, divisions, employees, positions, orgLevels, companyName]);

  const handleDrillInto = useCallback((divId: string) => {
    const div = divisions.find((d) => d.id === divId);
    if (!div) return;

    setDrillState((prev) => {
      if (prev.type === 'company') {
        return {
          type: 'division',
          divisionId: divId,
          breadcrumbs: [
            { id: null, label: companyName },
            { id: divId, label: div.name },
          ],
        };
      }
      // Drilling deeper from a division view
      return {
        type: 'division',
        divisionId: divId,
        breadcrumbs: [
          ...prev.breadcrumbs,
          { id: divId, label: div.name },
        ],
      };
    });
  }, [divisions, companyName]);

  const handleBreadcrumbClick = useCallback((crumb: Crumb) => {
    if (crumb.id === null) {
      // Navigate to company root
      setDrillState({ type: 'company' });
    } else {
      const divId = crumb.id!; // guaranteed non-null in else branch
      setDrillState((prev) => {
        if (prev.type !== 'division') return prev;
        const idx = prev.breadcrumbs.findIndex((b) => b.id === divId);
        if (idx < 0) return prev;
        return {
          type: 'division',
          divisionId: divId,
          breadcrumbs: prev.breadcrumbs.slice(0, idx + 1),
        };
      });
    }
  }, [divisions]);

  const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    if (drillState.type === 'company') {
      if (node.type === 'division') {
        handleDrillInto(node.id);
      }
    } else {
      if (node.type === 'division') {
        // Sub-division node in drill-down view — drill deeper
        handleDrillInto(node.id);
      } else if (node.type === 'person') {
        const emp = (node.data as PersonNodeData).employee;
        navigate({ to: '/org/employees/$employeeId/' as never, params: { employeeId: emp.id } as never });
      }
      // divroot node — no-op
    }
  }, [drillState, handleDrillInto, navigate]);

  if (divisions.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 text-center">
        <Building2 size={40} className="text-gray-200" />
        <div>
          <p className="text-sm font-medium text-gray-500">No divisions yet</p>
          <p className="text-xs text-gray-400 mt-1">Add a division to start building your org chart</p>
        </div>
        {canManage && (
          <button type="button" onClick={onAddDiv}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus size={14} /> Add Division
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb bar */}
      {drillState.type === 'division' && (
        <div className="flex items-center gap-1 px-4 py-2 bg-white border-b border-gray-200 text-sm flex-shrink-0">
          {drillState.breadcrumbs.map((crumb, idx) => {
            const isLast = idx === drillState.breadcrumbs.length - 1;
            return (
              <span key={crumb.id ?? '__root__'} className="flex items-center gap-1">
                {idx > 0 && <ChevronRight size={12} className="text-gray-400" />}
                {isLast ? (
                  <span className="font-semibold text-blue-700">{crumb.label}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleBreadcrumbClick(crumb)}
                    className="text-gray-500 hover:text-blue-600 hover:underline"
                  >
                    {crumb.label}
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* React Flow canvas */}
      <div className="flex-1 min-h-0">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          onNodeClick={handleNodeClick}
          fitView
          fitViewOptions={{ padding: 0.15, maxZoom: 1 }}
          minZoom={0.2}
          maxZoom={1.5}
          nodesDraggable
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#e2e8f0" gap={20} size={1} />
          <Controls showInteractive={false} className="!shadow-md !rounded-lg" />
          <MiniMap
            nodeColor={(n) => {
              if (n.type === 'root' || n.type === 'divroot') return '#0d9488';
              if (n.type === 'division') return '#3b82f6';
              return '#e5e7eb';
            }}
            maskColor="rgba(248,250,252,0.7)"
            pannable
            zoomable
            className="!shadow-md !rounded-lg"
          />
          {canManage && (
            <Panel position="top-right">
              <button type="button" onClick={onAddDiv}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 text-gray-600">
                <Plus size={12} /> Division
              </button>
            </Panel>
          )}
        </ReactFlow>
      </div>
    </div>
  );
}

// ─── Modals ───────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-gray-800">{title}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

function AddDivisionModal({ divisions, companyId, onClose, onSuccess }: {
  divisions: DivisionDto[]; companyId: string;
  onClose: () => void; onSuccess: (d: DivisionDto) => void;
}) {
  const [form, setForm] = useState({ code: '', name: '', parentDivisionId: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setLoading(true); setError('');
    const res = await createDivision({ companyId, code: form.code, name: form.name, parentDivisionId: form.parentDivisionId || undefined });
    setLoading(false);
    if (res.success && res.data) { toast.success('Division created'); onSuccess(res.data.division); }
    else setError(res.error ?? 'Failed');
  }

  return (
    <Modal title="Add Division" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Code"><input required className={inputCls} placeholder="e.g. ENG" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} /></Field>
        <Field label="Name"><input required className={inputCls} placeholder="e.g. Engineering" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></Field>
        <Field label="Parent Division (optional)">
          <select className={inputCls} value={form.parentDivisionId} onChange={(e) => setForm((f) => ({ ...f, parentDivisionId: e.target.value }))}>
            <option value="">— None (top-level) —</option>
            {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={loading} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{loading ? 'Adding…' : 'Add'}</button>
        </div>
      </form>
    </Modal>
  );
}

function EditDivisionModal({ division, companyId, onClose, onSuccess }: {
  division: DivisionDto; companyId: string;
  onClose: () => void; onSuccess: (d: DivisionDto) => void;
}) {
  const [name, setName] = useState(division.name);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleRename(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || name.trim() === division.name) { onClose(); return; }
    setLoading(true); setError('');
    const res = await renameDivision(division.id, name.trim(), companyId);
    setLoading(false);
    if (res.success && res.data) { toast.success('Division renamed'); onSuccess(res.data.division); }
    else setError(res.error ?? 'Failed to rename');
  }

  async function handleToggleStatus() {
    setLoading(true); setError('');
    const res = await setDivisionStatus(division.id, !division.isActive, companyId);
    setLoading(false);
    if (res.success && res.data) {
      toast.success(division.isActive ? 'Division deactivated' : 'Division reactivated');
      onSuccess(res.data.division);
    } else setError(res.error ?? 'Failed to update status');
  }

  return (
    <Modal title={`Edit Division — ${division.name}`} onClose={onClose}>
      <form onSubmit={handleRename} className="space-y-4">
        <Field label="Name">
          <input
            required
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={loading} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{loading ? 'Saving…' : 'Rename'}</button>
        </div>
        <div className="border-t pt-3">
          <button
            type="button"
            onClick={handleToggleStatus}
            disabled={loading}
            className={`w-full px-4 py-2 text-sm rounded-lg border disabled:opacity-50 ${division.isActive ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}
          >
            {division.isActive ? 'Deactivate Division' : 'Reactivate Division'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AddPositionModal({ divisions, orgLevels, companyId, onClose, onSuccess }: {
  divisions: DivisionDto[]; orgLevels: OrgLevelDto[]; companyId: string;
  onClose: () => void; onSuccess: (p: PositionDto) => void;
}) {
  const [form, setForm] = useState({ code: '', title: '', description: '', divisionId: '', orgLevelId: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setLoading(true); setError('');
    const res = await createPosition({
      companyId,
      code: form.code,
      title: form.title,
      description: form.description || undefined,
      divisionId: form.divisionId || undefined,
      orgLevelId: form.orgLevelId || undefined,
    });
    setLoading(false);
    if (res.success && res.data) { toast.success('Position created'); onSuccess(res.data.position); }
    else setError(res.error ?? 'Failed');
  }

  return (
    <Modal title="Add Position" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code"><input required className={inputCls} placeholder="e.g. SWE-L1" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} /></Field>
          <Field label="Title"><input required className={inputCls} placeholder="e.g. Software Engineer" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></Field>
        </div>
        <Field label="Division (optional)">
          <select className={inputCls} value={form.divisionId} onChange={(e) => setForm((f) => ({ ...f, divisionId: e.target.value }))}>
            <option value="">— None —</option>
            {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Org Level (optional)">
          <select className={inputCls} value={form.orgLevelId} onChange={(e) => setForm((f) => ({ ...f, orgLevelId: e.target.value }))}>
            <option value="">— None —</option>
            {orgLevels.sort((a, b) => a.level - b.level).map((l) => <option key={l.id} value={l.id}>L{l.level} – {l.name}</option>)}
          </select>
        </Field>
        <Field label="Description (optional)"><textarea rows={2} className={inputCls} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></Field>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={loading} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{loading ? 'Adding…' : 'Add'}</button>
        </div>
      </form>
    </Modal>
  );
}

function InviteModal({ divisions, positions, orgLevels, companyId, onClose, onSuccess }: {
  divisions: DivisionDto[]; positions: PositionDto[]; orgLevels: OrgLevelDto[]; companyId: string;
  onClose: () => void; onSuccess: (user: UserDto, tempPw: string) => void;
}) {
  const [form, setForm] = useState({ email: '', name: '', role: 'employee' as UserRole, divisionId: '', positionId: '', orgLevelId: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setLoading(true); setError('');
    const res = await inviteUser({ companyId, email: form.email, name: form.name, role: form.role, divisionId: form.divisionId || undefined });
    if (res.success && res.data) {
      const userId = res.data.user.id;
      // Assign position and org level if selected
      const assignments: Promise<unknown>[] = [];
      if (form.positionId) assignments.push(updateUserPosition(userId, companyId, form.positionId));
      if (form.orgLevelId) assignments.push(updateUserOrgLevel(userId, companyId, form.orgLevelId));
      if (assignments.length) await Promise.allSettled(assignments);
      toast.success(`Account created for ${res.data.user.name}`);
      onSuccess(res.data.user, res.data.temporaryPassword);
    } else {
      setError(res.error ?? 'Failed');
    }
    setLoading(false);
  }

  return (
    <Modal title="Create Account" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full Name"><input required className={inputCls} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></Field>
        <Field label="Email"><input required type="email" className={inputCls} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></Field>
        <Field label="Role">
          <select className={inputCls} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserRole }))}>
            <option value="employee">Employee</option>
            <option value="manager">Manager</option>
            <option value="hr">HR</option>
            <option value="admin">Company Admin</option>
          </select>
        </Field>
        <Field label="Division (optional)">
          <select className={inputCls} value={form.divisionId} onChange={(e) => setForm((f) => ({ ...f, divisionId: e.target.value }))}>
            <option value="">— None —</option>
            {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Position (optional)">
          <select className={inputCls} value={form.positionId} onChange={(e) => setForm((f) => ({ ...f, positionId: e.target.value }))}>
            <option value="">— None —</option>
            {positions.filter((p) => p.isActive).map((p) => <option key={p.id} value={p.id}>{p.title} ({p.code})</option>)}
          </select>
        </Field>
        <Field label="Org Level (optional)">
          <select className={inputCls} value={form.orgLevelId} onChange={(e) => setForm((f) => ({ ...f, orgLevelId: e.target.value }))}>
            <option value="">— None —</option>
            {orgLevels.sort((a, b) => a.level - b.level).map((l) => <option key={l.id} value={l.id}>L{l.level} – {l.name}</option>)}
          </select>
        </Field>
        <p className="text-xs text-gray-400">No email will be sent. Share the temporary password directly with the employee.</p>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={loading} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{loading ? 'Creating…' : 'Create Account'}</button>
        </div>
      </form>
    </Modal>
  );
}

function AssignModal({ employee, divisions, positions, orgLevels, companyId, onClose, onSaved }: {
  employee: UserDto; divisions: DivisionDto[]; positions: PositionDto[]; orgLevels: OrgLevelDto[];
  companyId: string; onClose: () => void; onSaved: () => void;
}) {
  const [divisionId, setDivisionId] = useState(employee.divisionId ?? '');
  const [positionId, setPositionId] = useState(employee.positionId ?? '');
  const [orgLevelId, setOrgLevelId] = useState(employee.orgLevelId ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    setLoading(true); setError('');
    try {
      await Promise.all([
        assignDivision(employee.id, companyId, divisionId || null),
        updateUserPosition(employee.id, companyId, positionId || null),
        updateUserOrgLevel(employee.id, companyId, orgLevelId || null),
      ]);
      toast.success(`Assignments saved for ${employee.name}`);
      onSaved();
    } catch { setError('Failed to save. Please try again.'); }
    setLoading(false);
  }

  return (
    <Modal title={`Assign — ${employee.name}`} onClose={onClose}>
      <div className="space-y-4">
        <Field label="Division">
          <select className={inputCls} value={divisionId} onChange={(e) => setDivisionId(e.target.value)}>
            <option value="">— None —</option>
            {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Position">
          <select className={inputCls} value={positionId} onChange={(e) => setPositionId(e.target.value)}>
            <option value="">— None —</option>
            {positions.filter((p) => p.isActive).map((p) => <option key={p.id} value={p.id}>{p.title} ({p.code})</option>)}
          </select>
        </Field>
        <Field label="Org Level">
          <select className={inputCls} value={orgLevelId} onChange={(e) => setOrgLevelId(e.target.value)}>
            <option value="">— None —</option>
            {orgLevels.sort((a, b) => a.level - b.level).map((l) => <option key={l.id} value={l.id}>L{l.level} – {l.name}</option>)}
          </select>
        </Field>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={save} disabled={loading} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{loading ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function OrgPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <OrgContent />
    </ProtectedRoute>
  );
}

const ROLE_BADGE: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-700', hr: 'bg-blue-100 text-blue-700',
  manager: 'bg-amber-100 text-amber-700', employee: 'bg-gray-100 text-gray-600',
};

function OrgContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const canManage = can(user?.role, 'manage:org');
  const canInvite = can(user?.role, 'invite:users');

  const [tab, setTab] = useState<'organogram' | 'positions' | 'employees'>('organogram');
  const [divisions, setDivisions]   = useState<DivisionDto[]>([]);
  const [positions, setPositions]   = useState<PositionDto[]>([]);
  const [employees, setEmployees]   = useState<UserDto[]>([]);
  const [orgLevels, setOrgLevels]   = useState<OrgLevelDto[]>([]);
  const [loading, setLoading]       = useState(true);

  const [showAddDiv,   setShowAddDiv]    = useState(false);
  const [editDivision, setEditDivision]  = useState<DivisionDto | null>(null);
  const [showAddPos,   setShowAddPos]    = useState(false);
  const [showInvite,   setShowInvite]    = useState(false);
  const [assignTarget, setAssignTarget]  = useState<UserDto | null>(null);
  const [inviteResult, setInviteResult]  = useState<{ user: UserDto; tempPw: string } | null>(null);

  const [empDivFilter, setEmpDivFilter] = useState('');
  const [empSearch,    setEmpSearch]    = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [divRes, posRes, empRes, lvlRes] = await Promise.all([
      listDivisions(companyId),
      listPositions(companyId, false),
      listUsers(companyId, { isActive: true }),
      listOrgLevels(companyId),
    ]);
    if (divRes.success && divRes.data) setDivisions(divRes.data.divisions);
    if (posRes.success && posRes.data) setPositions(posRes.data.positions);
    if (empRes.success && empRes.data) setEmployees(empRes.data.users);
    if (lvlRes.success && lvlRes.data) setOrgLevels(lvlRes.data.orgLevels);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filteredEmps = employees.filter((e) => {
    if (empDivFilter && e.divisionId !== empDivFilter) return false;
    if (empSearch && !e.name.toLowerCase().includes(empSearch.toLowerCase()) && !e.email.toLowerCase().includes(empSearch.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organization</h1>
          <p className="text-sm text-gray-500 mt-1">
            {divisions.length} divisions · {positions.length} positions · {employees.length} employees
          </p>
        </div>
        <div className="flex gap-2">
          {canManage && (
            <>
              <button type="button" onClick={() => setShowAddDiv(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"><Plus size={14} /> Division</button>
              <button type="button" onClick={() => setShowAddPos(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"><Plus size={14} /> Position</button>
            </>
          )}
          {canInvite && (
            <button type="button" onClick={() => setShowInvite(true)} className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"><UserPlus size={14} /> Add Employee</button>
          )}
        </div>
      </div>

      {/* Invite success banner */}
      {inviteResult && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-green-800">Account created for <strong>{inviteResult.user.name}</strong></p>
            <p className="text-xs text-green-700 mt-0.5">Temporary password: <code className="bg-green-100 px-1 rounded font-mono">{inviteResult.tempPw}</code></p>
          </div>
          <button type="button" onClick={() => setInviteResult(null)} className="text-green-500 hover:text-green-700"><X size={16} /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b mb-0">
        {([
          { key: 'organogram', label: 'Organogram', icon: Building2 },
          { key: 'positions',  label: 'Positions',  icon: Briefcase  },
          { key: 'employees',  label: 'Employees',  icon: Users       },
        ] as const).map(({ key, label, icon: Icon }) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* ── ORGANOGRAM TAB ─────────────────────────────────────────────────── */}
      {tab === 'organogram' && (
        <div
          className="border border-gray-200 rounded-b-xl rounded-tr-xl bg-slate-50"
          style={{ height: '600px', overflow: 'clip' }}
        >
          <Organogram
            divisions={divisions}
            employees={employees}
            positions={positions}
            orgLevels={orgLevels}
            companyName="Company"
            canManage={canManage}
            onAddDiv={() => setShowAddDiv(true)}
          />
        </div>
      )}

      {/* ── POSITIONS TAB ──────────────────────────────────────────────────── */}
      {tab === 'positions' && (
        <div className="mt-6">
          {/* Divisions list with edit/deactivate */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">Divisions ({divisions.length})</p>
              {canManage && (
                <button type="button" onClick={() => setShowAddDiv(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-lg text-gray-600 hover:bg-gray-50"><Plus size={12} /> Add</button>
              )}
            </div>
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Code</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Status</th>
                    {canManage && <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {divisions.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-400">No divisions yet.</td></tr>
                  ) : divisions.map((div) => (
                    <tr key={div.id} className={`hover:bg-gray-50 ${!div.isActive ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{div.name}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-500 hidden md:table-cell">{div.code}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${div.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {div.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {canManage && (
                        <td className="px-4 py-2.5">
                          <button
                            type="button"
                            onClick={() => setEditDivision(div)}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                          >
                            <Pencil size={11} /> Edit
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">{positions.length} position{positions.length !== 1 ? 's' : ''}</p>
            {canManage && (
              <button type="button" onClick={() => setShowAddPos(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"><Plus size={14} /> Add Position</button>
            )}
          </div>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Division</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Org Level</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Headcount</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {positions.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">No positions defined yet.</td></tr>
                ) : positions.map((pos) => {
                  const div = divisions.find((d) => d.id === pos.divisionId);
                  const lvl = orgLevels.find((l) => l.id === pos.orgLevelId);
                  const headcount = employees.filter((e) => e.positionId === pos.id).length;
                  return (
                    <tr key={pos.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{pos.code}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{pos.title}</td>
                      <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{div?.name ?? <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{lvl ? `L${lvl.level} – ${lvl.name}` : <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 text-gray-600">{headcount}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${pos.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{pos.isActive ? 'Active' : 'Inactive'}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── EMPLOYEES TAB ──────────────────────────────────────────────────── */}
      {tab === 'employees' && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input type="search" placeholder="Search name or email…"
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-56"
              value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} />
            <select className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={empDivFilter} onChange={(e) => setEmpDivFilter(e.target.value)}>
              <option value="">All Divisions</option>
              {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <p className="text-sm text-gray-500 ml-auto">{filteredEmps.length} employee{filteredEmps.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Division</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Position</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Level</th>
                  {canManage && <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredEmps.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">No employees found.</td></tr>
                ) : filteredEmps.map((emp) => {
                  const div = divisions.find((d) => d.id === emp.divisionId);
                  const pos = positions.find((p) => p.id === emp.positionId);
                  const lvl = orgLevels.find((l) => l.id === emp.orgLevelId);
                  return (
                    <tr key={emp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-xs font-semibold text-blue-700 shrink-0">
                            {emp.name.charAt(0).toUpperCase()}
                          </div>
                          <Link to={'/org/employees/$employeeId/' as any} params={{ employeeId: emp.id } as any} className="font-medium text-gray-800 hover:text-blue-600 hover:underline">{emp.name}</Link>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">{emp.email}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${ROLE_BADGE[emp.role] ?? 'bg-gray-100 text-gray-600'}`}>{emp.role}</span></td>
                      <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{div?.name ?? <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">{pos?.title ?? <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">{lvl ? `L${lvl.level} – ${lvl.name}` : <span className="text-gray-300">—</span>}</td>
                      {canManage && (
                        <td className="px-4 py-3 flex items-center gap-2">
                          <button type="button" onClick={() => setAssignTarget(emp)} className="text-xs text-blue-600 hover:underline">Assign</button>
                          <Link to={'/org/employees/$employeeId/' as any} params={{ employeeId: emp.id } as any} className="text-xs text-gray-500 hover:underline">Profile</Link>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {showAddDiv && <AddDivisionModal divisions={divisions} companyId={companyId} onClose={() => setShowAddDiv(false)} onSuccess={(d) => { setDivisions((p) => [...p, d]); setShowAddDiv(false); }} />}
      {editDivision && <EditDivisionModal division={editDivision} companyId={companyId} onClose={() => setEditDivision(null)} onSuccess={(d) => { setDivisions((p) => p.map((x) => x.id === d.id ? d : x)); setEditDivision(null); }} />}
      {showAddPos && <AddPositionModal divisions={divisions} orgLevels={orgLevels} companyId={companyId} onClose={() => setShowAddPos(false)} onSuccess={(p) => { setPositions((prev) => [...prev, p]); setShowAddPos(false); }} />}
      {showInvite && <InviteModal divisions={divisions} positions={positions} orgLevels={orgLevels} companyId={companyId} onClose={() => setShowInvite(false)} onSuccess={(u, tempPw) => { setShowInvite(false); setInviteResult({ user: u, tempPw }); setEmployees((p) => [...p, u]); }} />}
      {assignTarget && <AssignModal employee={assignTarget} divisions={divisions} positions={positions} orgLevels={orgLevels} companyId={companyId} onClose={() => setAssignTarget(null)} onSaved={() => { setAssignTarget(null); loadAll(); }} />}
    </div>
  );
}
